import { useState } from 'react'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { useTranslation } from 'react-i18next'
import { AlertCircleIcon } from 'lucide-react'
import {
	Alert, AlertDescription,
	Form, FormControl, FormMessage, FormDialog, FormField, FormItem, FormLabel,
	Input, PhoneInput,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Dialog, DialogTitle, DialogDescription, DialogContent, DialogFooter,
	Button,
} from '@rentflowdev/ui'
import { useCreateContactMutation, useCreateDealMutation, useListPropertiesQuery } from '@/api'
import { formatPropertyAddress } from '@/lib/property-address'
import { usePersistedForm } from '@/lib/use-persisted-form'
import { extractErrorMessage } from '@/lib/error-message'
import type { Property } from '@/types'
import {
	createDealSchema, DEFAULT_VALUES,
	type CreateDealFormValues,
} from './schema'

const DRAFT_KEY = 'deal:create'

interface CreateDealDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
}

interface DuplicateContactConflict {
	existingContactId: string
	existingContactName: string
	phone?: string
}

function formatPropertyLabel(property: Property, t: (key: string, opts?: Record<string, unknown>) => string): string {
	const parts: string[] = []
	if (property.rooms) parts.push(t('properties.roomsSuffix', { rooms: property.rooms }))
	parts.push(t(`properties.types.${property.type}`).toLowerCase())
	parts.push(formatPropertyAddress(property.city, property.address))
	return parts.join(' ')
}

export function CreateDealDialog({ open, onOpenChange }: CreateDealDialogProps) {
	const { t } = useTranslation()
	const { data: propertiesData } = useListPropertiesQuery()
	const [createContact] = useCreateContactMutation()
	const [createDeal, { isLoading: isCreating }] = useCreateDealMutation()

	const [submitError, setSubmitError] = useState<string | null>(null)
	const [conflict, setConflict] = useState<DuplicateContactConflict | null>(null)
	const [formValues, setFormValues] = useState<CreateDealFormValues | null>(null)
	const [isResolving, setIsResolving] = useState(false)

	const { form, resetDraft } = usePersistedForm<CreateDealFormValues>({
		key:           DRAFT_KEY,
		defaultValues: DEFAULT_VALUES,
		resolver:      zodResolverI18n(createDealSchema),
		mode:          'onBlur',
		active:        open,
	})

	const properties = propertiesData?.properties ?? []

	async function onSubmit(values: CreateDealFormValues) {
		setSubmitError(null)
		setConflict(null)
		setFormValues(values)

		try {
			const contactRes: any = await createContact({
				name: values.clientName,
				type: 'TENANT',
				...(values.clientPhone ? { phone: values.clientPhone } : {}),
				...(values.clientEmail ? { email: values.clientEmail } : {}),
			}).unwrap()

			if (!contactRes) throw new Error('createContact returned null')

			// Check if we got a conflict instead of a contact
			if (contactRes.conflict) {
				setConflict(contactRes.conflict)
				return
			}

			// We have a contact, proceed to create deal
			if (!contactRes.contact) {
				throw new Error('Invalid contact response')
			}

			await createDeal({
				contactId: contactRes.contact.id,
				propertyId: values.propertyId,
			}).unwrap()

			resetDraft()
			form.reset(DEFAULT_VALUES)
			onOpenChange(false)
		} catch (err) {
			console.error('[CreateDeal] FAILED', err)
			setSubmitError(extractErrorMessage(err) ?? t('deals.createDialog.errorGeneric'))
		}
	}

	async function handleUseExisting() {
		if (!conflict || !formValues) return
		setIsResolving(true)
		try {
			await createDeal({
				contactId: conflict.existingContactId,
				propertyId: formValues.propertyId,
			}).unwrap()

			setConflict(null)
			setFormValues(null)
			resetDraft()
			form.reset(DEFAULT_VALUES)
			onOpenChange(false)
		} catch (err) {
			console.error('[CreateDeal] FAILED', err)
			setSubmitError(extractErrorMessage(err) ?? t('deals.createDialog.errorGeneric'))
		} finally {
			setIsResolving(false)
		}
	}

	async function handleCreateNew() {
		if (!formValues) return
		setIsResolving(true)
		try {
			// Create contact without phone to avoid duplicate check
			const contactRes: any = await createContact({
				name: formValues.clientName,
				type: 'TENANT',
				...(formValues.clientEmail ? { email: formValues.clientEmail } : {}),
			}).unwrap()

			if (!contactRes || !contactRes.contact) {
				throw new Error('Failed to create contact')
			}

			await createDeal({
				contactId: contactRes.contact.id,
				propertyId: formValues.propertyId,
			}).unwrap()

			setConflict(null)
			setFormValues(null)
			resetDraft()
			form.reset(DEFAULT_VALUES)
			onOpenChange(false)
		} catch (err) {
			console.error('[CreateDeal] FAILED', err)
			setSubmitError(extractErrorMessage(err) ?? t('deals.createDialog.errorGeneric'))
		} finally {
			setIsResolving(false)
		}
	}

	return (
		<Form {...form}>
			<FormDialog
				open={open}
				onOpenChange={onOpenChange}
				title={t('deals.createDialog.title')}
				onSubmit={form.handleSubmit(onSubmit)}
				submitLabel={isCreating ? t('deals.createDialog.creating') : t('deals.createDialog.create')}
				cancelLabel={t('deals.createDialog.cancel')}
				closeAriaLabel={t('common.close')}
				isSubmitting={isCreating}
			>
				{submitError && (
					<Alert variant="destructive">
						<AlertCircleIcon className="size-4" />
						<AlertDescription>{submitError}</AlertDescription>
					</Alert>
				)}
				<FormField
					control={form.control}
					name="propertyId"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('deals.createDialog.property')}</FormLabel>
							<Select onValueChange={field.onChange} value={field.value}>
								<FormControl>
									<SelectTrigger width="full" className="h-auto px-4 py-[10px] text-sm">
										<SelectValue placeholder={t('deals.createDialog.propertyPlaceholder')} />
									</SelectTrigger>
								</FormControl>
								<SelectContent>
									{properties.map((p) => (
										<SelectItem key={p.id} value={p.id}>
											{formatPropertyLabel(p, t)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="clientName"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('deals.createDialog.clientName')}</FormLabel>
							<FormControl>
								<Input
									placeholder={t('deals.createDialog.clientNamePlaceholder')}
									{...field}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="clientPhone"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('deals.createDialog.clientPhone')}</FormLabel>
							<FormControl>
								<PhoneInput value={field.value ?? ''} onChange={field.onChange} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="clientEmail"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('deals.createDialog.clientEmail')}</FormLabel>
							<FormControl>
								<Input
									type="email"
									autoComplete="email"
									placeholder={t('deals.createDialog.clientEmailPlaceholder')}
									{...field}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
			</FormDialog>

			{/* Conflict resolution modal */}
			<Dialog open={!!conflict} onOpenChange={(isOpen) => !isOpen && setConflict(null)}>
				<DialogContent className="max-w-sm">
					<DialogTitle>
						{t('deals.createDialog.duplicateContact.title')}
					</DialogTitle>
					<DialogDescription className="space-y-3">
						<p>
							{t('deals.createDialog.duplicateContact.description', {
								name: conflict?.existingContactName,
								phone: conflict?.phone,
							})}
						</p>
					</DialogDescription>
					<DialogFooter className="gap-2 sm:gap-0">
						<Button
							variant="outline"
							onClick={() => setConflict(null)}
							disabled={isResolving}
						>
							{t('deals.createDialog.duplicateContact.cancel')}
						</Button>
						<Button
							variant="outline"
							onClick={handleCreateNew}
							disabled={isResolving}
							loading={isResolving}
						>
							{t('deals.createDialog.duplicateContact.createNew')}
						</Button>
						<Button
							onClick={handleUseExisting}
							disabled={isResolving}
							loading={isResolving}
						>
							{t('deals.createDialog.duplicateContact.useExisting')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</Form>
	)
}
